import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Authenticator } from '@aws-amplify/ui-react';
import { getCurrentUser } from 'aws-amplify/auth';

// UI Components
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import About from './components/About';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import HomePage from './components/HomePage';
import AuthComponent from './components/AuthComponent';
import CharacterCreation from './components/CharacterCreation';
import SkillsBasedCharacterCreation from './components/SkillsBasedCharacterCreation';
import CharacterList from './components/CharacterList';
import EditCharacter from './components/EditCharacter';
import GameMenu from './components/game/menu/GameMenu';
import PartySelection from './components/game/party/PartySelection';
import { WintermereGame } from './games/wintermere';
import { DarkForestGame } from './games/darkforest';
import GamePlay from './components/game/play/GamePlay';
import AccountInfo from './components/AccountInfo';
import BlogList from './components/BlogList';
import BlogPost from './components/BlogPost';
import ContentManagement from './components/ContentManagement';
import GoogleAnalyticsTracker from './components/GoogleAnalyticsTracker';
import ModuleAdmin from './components/ModuleAdmin';
import GameModuleForm from './components/GameModuleForm';
import GameModuleList from './components/GameModuleList';
import GameModuleView from './components/GameModuleView';
import Tavern from './components/Tavern';
import TavernCategory from './components/TavernCategory';
import TavernThread from './components/TavernThread';
import TavernThreadForm from './components/TavernThreadForm';

// Context providers
import { CharacterProvider } from './contexts/CharacterContext';

// Utilities
import { checkAdminStatus, checkCreatorStatus } from './utils/authUtils';

// Styles
import '@aws-amplify/ui-react/styles.css'; // Import the styles
import './App.css';


const LoadingScreen = () => (
  <div className="loading">Loading...</div>
);

const ProtectedRoute = ({ user, children }) => {
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  return children;
};

const AdminRouteWrapper = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkAdmin() {
      try {
        const adminStatus = await checkAdminStatus();
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Error in AdminRouteWrapper:', error);
      } finally {
        setLoading(false);
      }
    }
    checkAdmin();
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return isAdmin ? children : <Navigate to="/" replace />;
};

const CreatorRouteWrapper = ({ children }) => {
  const [isCreator, setIsCreator] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkCreator() {
      try {
        const creatorStatus = await checkCreatorStatus();
        setIsCreator(creatorStatus);
      } catch (error) {
        console.error('Error in CreatorRouteWrapper:', error);
      } finally {
        setLoading(false);
      }
    }
    checkCreator();
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return isCreator ? children : <Navigate to="/" replace />;
};

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  async function checkUser() {
    try {
      const authenticatedUser = await getCurrentUser();
      setUser(authenticatedUser);
    } catch (err) {
      console.log('Error getting current user:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Authenticator.Provider>
    <Router>
      <CharacterProvider>
        <div className="app-container">
          <GoogleAnalyticsTracker />
          <Navigation user={user} setUser={setUser} />
          <main className="main-content">
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<About />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/auth" element={<AuthComponent setUser={setUser} />} />
                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:id" element={<BlogPost />} />
                {/* Party selection phase */}
                <Route path="/party/:gameId/selection" element={<PartySelection />} />
                {/* Game play phase */}
                <Route path="/play/:gameId" element={<GamePlay />} />
                
                {/* Game Routes */}
                <Route 
                  path="/games" 
                  element={
                    <ProtectedRoute user={user}>
                      <GameMenu />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/modules" 
                  element={
                    <ProtectedRoute user={user}>
                      <AdminRouteWrapper>
                        <ModuleAdmin />
                      </AdminRouteWrapper>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/games/wintermere/play/:characterId" 
                  element={
                    <ProtectedRoute user={user}>
                      <WintermereGame />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/games/:gameId/create-character"
                  element={
                    <ProtectedRoute user={user}>
                      <SkillsBasedCharacterCreation />
                    </ProtectedRoute>
                  }
                />
                
                {/* Character Management Routes */}
                <Route 
                  path="/characters" 
                  element={
                    <ProtectedRoute user={user}>
                      <CharacterList />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/create-character"
                  element={
                    <ProtectedRoute user={user}>
                      <SkillsBasedCharacterCreation />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/edit-character/:id" 
                  element={
                    <ProtectedRoute user={user}>
                      <EditCharacter />
                    </ProtectedRoute>
                  } 
                />
                
                {/* User Account Routes */}
                <Route 
                  path="/account" 
                  element={
                    <ProtectedRoute user={user}>
                      <AccountInfo user={user} />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/content-management" 
                  element={
                    <ProtectedRoute user={user}>
                      <AdminRouteWrapper>
                        <ContentManagement />
                      </AdminRouteWrapper>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Game Module Routes */}
                <Route 
                  path="/modules" 
                  element={
                    <ProtectedRoute user={user}>
                      <GameModuleList />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/modules/create" 
                  element={
                    <ProtectedRoute user={user}>
                      <CreatorRouteWrapper>
                        <GameModuleForm />
                      </CreatorRouteWrapper>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/modules/edit/:id" 
                  element={
                    <ProtectedRoute user={user}>
                      <CreatorRouteWrapper>
                        <GameModuleForm />
                      </CreatorRouteWrapper>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/modules/:id" 
                  element={
                    <ProtectedRoute user={user}>
                      <GameModuleView />
                    </ProtectedRoute>
                  } 
                />

                <Route
                  path="/games/wintermere/play/:characterId"
                  element={
                    <ProtectedRoute user={user}>
                      <WintermereGame />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/games/darkforest/play/:characterId"
                  element={
                    <ProtectedRoute user={user}>
                      <DarkForestGame />
                    </ProtectedRoute>
                  }
                />

                <Route path="/tavern" element={<Tavern />} />
                <Route path="/tavern/category/:categoryId" element={<TavernCategory />} />
                <Route path="/tavern/thread/:threadId" element={<TavernThread />} />
                <Route path="/tavern/category/:categoryId/new-thread" element={<TavernThreadForm />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
      </CharacterProvider>
    </Router>
    </Authenticator.Provider>
  );
};

export default App;